<template>
  <div class="catalog-page">
    <el-button class="mobile-filter-toggle" size="small" @click="mobileFiltersOpen = !mobileFiltersOpen">
      <span><i class="el-icon-search"></i> 筛选作品<span v-if="activeFilterCount" class="filter-count">{{ activeFilterCount }}</span></span>
      <i :class="mobileFiltersOpen ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
    </el-button>

    <div class="catalog-layout">
      <el-card class="filter-card" :class="{ 'mobile-open': mobileFiltersOpen }" shadow="never">
        <div slot="header" class="filter-heading">
          <strong>筛选目录</strong>
          <span>{{ activeFilterCount ? activeFilterCount + ' 项条件' : '全部作品' }}</span>
        </div>
        <app-scrollbar class="filter-scroll">
          <el-form :model="queryObject" label-position="top" size="mini" class="filter-form" @submit.native.prevent="search">
            <el-form-item label="DLsite 编号">
              <el-input v-model.trim="queryObject.gameId" clearable placeholder="例如 RJ123456" prefix-icon="el-icon-search" @keyup.enter.native="search"></el-input>
            </el-form-item>
            <el-form-item label="作品名称">
              <el-input v-model.trim="queryObject.name" clearable placeholder="搜索作品名称" @keyup.enter.native="search"></el-input>
            </el-form-item>
            <el-form-item label="社团">
              <el-select v-model="queryObject.author" filterable clearable placeholder="选择社团" popper-class="catalog-filter-popper">
                <el-option v-for="maker in makers" :key="maker.value" :value="maker.value" :label="maker.label"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="发售日期">
              <el-date-picker
                v-if="!isMobile"
                v-model="queryObject.releaseTime"
                type="daterange"
                align="right"
                unlink-panels
                value-format="yyyy-MM-dd"
                range-separator="至"
                start-placeholder="开始"
                end-placeholder="结束"
                :editable="false"
                :picker-options="pickerOptions"
                clearable
              ></el-date-picker>
              <div v-else class="mobile-date-range">
                <el-date-picker v-model="mobileStartDate" type="date" value-format="yyyy-MM-dd" placeholder="开始日期" :editable="false" clearable popper-class="catalog-mobile-date-popper"></el-date-picker>
                <el-date-picker v-model="mobileEndDate" type="date" value-format="yyyy-MM-dd" placeholder="结束日期" :editable="false" clearable popper-class="catalog-mobile-date-popper"></el-date-picker>
              </div>
              <div v-if="isMobile" class="mobile-date-shortcuts" aria-label="快捷日期范围">
                <el-button v-for="shortcut in pickerOptions.shortcuts" :key="shortcut.text" size="mini" plain @click="applyDateShortcut(shortcut.text)">{{ shortcut.text }}</el-button>
              </div>
            </el-form-item>
            <el-form-item label="游戏类型">
              <el-select v-model="queryObject.type" filterable clearable placeholder="全部类型" popper-class="catalog-filter-popper">
                <el-option v-for="type in types" :key="type.value" :value="type.value" :label="type.label"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="语言">
              <el-select v-model="queryObject.language" filterable clearable placeholder="全部语言" popper-class="catalog-filter-popper">
                <el-option v-for="language in languages" :key="language.value" :value="language.value" :label="language.label"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="游戏分类">
              <el-cascader
                v-model="queryObject.tags"
                :options="genres"
                :props="cascaderOptions"
                :show-all-levels="false"
                filterable
                clearable
                collapse-tags
                placeholder="最多选择 5 项"
                popper-class="catalog-filter-popper"
                @change="limitTags"
              ></el-cascader>
            </el-form-item>
            <div class="filter-actions">
              <el-button type="primary" size="small" @click="search">搜索作品</el-button>
              <el-button size="small" @click="resetFilters">重置</el-button>
            </div>
          </el-form>
        </app-scrollbar>
      </el-card>

      <el-card class="results-card" shadow="never" v-loading="loading">
        <div class="results-heading">
          <div class="results-title">
            <div class="section-kicker">GAME CATALOG</div>
            <h1>游戏目录</h1>
          </div>
          <div class="results-controls">
            <label>排序
              <el-select v-model="queryObject.orderBy" size="small" aria-label="排序依据" @change="fetchTable(1)">
                <el-option v-for="item in orderBy" :key="item.value" :label="item.label" :value="item.value"></el-option>
              </el-select>
            </label>
            <label>每页
              <el-select v-model="queryObject.pageSize" size="small" aria-label="每页数量" @change="fetchTable(1)">
                <el-option v-for="size in [25, 50, 75, 100]" :key="size" :label="String(size)" :value="size"></el-option>
              </el-select>
            </label>
            <el-button class="results-refresh" size="small" icon="el-icon-refresh" :loading="loading" aria-label="刷新目录作品" title="刷新" @click="refreshTable"><span>刷新</span></el-button>
          </div>
        </div>

        <div ref="tableSpace" class="table-space">
          <el-table ref="table" :data="tableData" :height="tableHeight" row-key="id" empty-text="没有找到符合条件的作品">
            <el-table-column label="作品" width="300">
              <template slot-scope="scope">
                <div class="table-work">
                  <el-image class="table-cover" :src="scope.row.cover" fit="contain" lazy>
                    <div slot="error" class="cover-empty"><i class="el-icon-picture-outline"></i></div>
                  </el-image>
                  <div class="table-work-text">
                    <div class="work-ids">
                      <a v-if="!scope.row.hasDelete" :href="workUrl(scope.row)" target="_blank" rel="noopener noreferrer" class="work-code">{{ scope.row.gameId }}</a>
                      <span v-else class="work-code deleted"><i class="el-icon-remove-outline"></i> {{ scope.row.gameId }}</span>
                      <span class="work-code" :title="`站内 ID：${scope.row.id}`">{{ scope.row.id }}</span>
                    </div>
                    <div class="work-name" :title="scope.row.name">{{ scope.row.name }}</div>
                    <div v-if="scope.row.translatedName" class="work-translation" :title="scope.row.translatedName">{{ scope.row.translatedName }}</div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="社团" width="150" show-overflow-tooltip>
              <template slot-scope="scope"><a v-if="scope.row.authorUrl" :href="scope.row.authorUrl" target="_blank" rel="noopener noreferrer">{{ scope.row.author }}</a><span v-else>{{ scope.row.author }}</span></template>
            </el-table-column>
            <el-table-column label="类型 / 语言" width="180">
              <template slot-scope="scope">
                <div class="table-tags">
                  <el-tag v-for="type in typeParts(scope.row.type)" :key="type" size="mini" type="success">{{ type }}</el-tag>
                  <el-tag v-if="scope.row.language" size="mini" type="info">{{ scope.row.language }}</el-tag>
                  <el-tag v-if="scope.row.isAI" size="mini" type="danger">AI</el-tag>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="releaseTime" label="发售日期" width="110"></el-table-column>
            <el-table-column prop="version" label="版本" width="135" show-overflow-tooltip></el-table-column>
            <el-table-column label="评分" width="105">
              <template slot-scope="scope"><span class="score"><i class="el-icon-star-on"></i> {{ formatScore(scope.row.score) }}</span><small class="delta" :class="{ negative: Number(scope.row.scoreDelta) < 0 }">{{ signedNumber(scope.row.scoreDelta) }}</small></template>
            </el-table-column>
            <el-table-column label="销量" width="115">
              <template slot-scope="scope">{{ formatNumber(scope.row.saleCount) }}<small class="delta" :class="{ negative: Number(scope.row.saleCountDelta) < 0 }">{{ signedNumber(scope.row.saleCountDelta) }}</small></template>
            </el-table-column>
            <el-table-column label="售价" width="105">
              <template slot-scope="scope"><strong class="price">¥{{ formatNumber(scope.row.price) }}</strong></template>
            </el-table-column>
            <el-table-column label="分类" min-width="240">
              <template slot-scope="scope"><div class="table-tags"><el-tag v-for="tag in scope.row.tags || []" :key="tag" size="mini" type="info">{{ tag }}</el-tag></div></template>
            </el-table-column>
          </el-table>
        </div>

        <app-scrollbar ref="mobileResults" class="mobile-results">
          <div v-if="!tableData.length && !loading" class="mobile-empty">没有找到符合条件的作品</div>
          <el-card v-for="game in tableData" :key="game.id" class="mobile-work-card" shadow="never" :body-style="{ padding: '10px' }">
            <div class="mobile-work-main">
              <div class="mobile-cover">
                <el-image :src="game.cover" fit="contain" lazy><div slot="error" class="cover-empty"><i class="el-icon-picture-outline"></i></div></el-image>
              </div>
              <div class="mobile-work-info">
                <div class="work-ids">
                  <a v-if="!game.hasDelete" :href="workUrl(game)" target="_blank" rel="noopener noreferrer" class="work-code">{{ game.gameId }}</a>
                  <span v-else class="work-code deleted">{{ game.gameId }} · 已下架</span>
                  <span class="work-code" :title="`站内 ID：${game.id}`">{{ game.id }}</span>
                </div>
                <strong class="mobile-work-name">{{ game.name }}</strong>
                <span v-if="game.translatedName" class="work-translation">{{ game.translatedName }}</span>
                <div class="table-tags"><el-tag v-for="type in typeParts(game.type).slice(0, 2)" :key="type" size="mini" type="success">{{ type }}</el-tag><el-tag v-if="game.language" size="mini" type="info">{{ game.language }}</el-tag></div>
              </div>
            </div>
            <div class="mobile-work-meta">
              <span>{{ game.releaseTime || '日期未知' }}<span v-if="game.version"> · {{ game.version }}</span></span>
              <span class="score"><i class="el-icon-star-on"></i> {{ formatScore(game.score) }} · {{ formatNumber(game.saleCount) }} 销量</span>
            </div>
            <div class="mobile-work-bottom">
              <a v-if="game.authorUrl" :href="game.authorUrl" target="_blank" rel="noopener noreferrer">{{ game.author }}</a><span v-else>{{ game.author }}</span>
              <strong class="price">¥{{ formatNumber(game.price) }}<small> JPY</small></strong>
            </div>
          </el-card>
        </app-scrollbar>

        <div class="results-footer">
          <span>已收录 <strong>{{ formatNumber(total) }}</strong> 部作品 · 第 {{ current || 1 }} / {{ totalPage || 1 }} 页</span>
          <el-pagination
            background
            :small="isMobile || isCompactLandscape"
            :layout="isMobile || isCompactLandscape ? 'prev, pager, next' : 'prev, pager, next, jumper'"
            :pager-count="isMobile || isCompactLandscape ? 5 : 7"
            :page-count="totalPage"
            :current-page="current || 1"
            :page-size="queryObject.pageSize"
            @current-change="fetchTable"
          ></el-pagination>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import AppScrollbar from '@/components/AppScrollbar.vue'

const emptyFilters = () => ({ gameId: '', name: '', author: null, releaseTime: null, type: null, language: null, tags: [] })
const dateShortcutLabels = ['今天', '昨天', '一周前', '本月', '上月', '本季', '今年', '去年']
const dateRangeFor = (label) => {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth()
  if (label === '今天') return [now, now]
  if (label === '昨天' || label === '一周前') {
    const start = new Date(now)
    start.setTime(start.getTime() - 24 * 60 * 60 * 1000 * (label === '昨天' ? 1 : 7))
    return [start, label === '昨天' ? start : now]
  }
  if (label === '本月') return [new Date(year, month, 1), new Date(year, month + 1, 0)]
  if (label === '上月') return [new Date(year, month - 1, 1), new Date(year, month, 0)]
  if (label === '本季') {
    const quarterStart = Math.floor(month / 3) * 3
    return [new Date(year, quarterStart, 1), new Date(year, quarterStart + 3, 0)]
  }
  if (label === '今年') return [new Date(year, 0, 1), new Date(year, 11, 31)]
  return [new Date(year - 1, 0, 1), new Date(year - 1, 11, 31)]
}
const formatLocalDate = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

export default {
  name: 'TableNew',
  components: { AppScrollbar },
  data() {
    return {
      queryObject: { ...emptyFilters(), pageNo: 1, pageSize: 25, orderBy: 'idDesc' },
      makers: [], types: [], languages: [], genres: [], orderBy: [], tableData: [],
      total: 0, totalPage: 0, current: 1, loading: false, requestId: 0, lastRequestQuery: null,
      mobileFiltersOpen: false, isMobile: false, isCompactLandscape: false, tableHeight: 320,
      tableObserver: null,
      cascaderOptions: { multiple: true, emitPath: false },
      pickerOptions: {
        shortcuts: dateShortcutLabels.map(text => ({ text, onClick(picker) { picker.$emit('pick', dateRangeFor(text)) } }))
      }
    }
  },
  computed: {
    mobileStartDate: {
      get() { return this.queryObject.releaseTime ? this.queryObject.releaseTime[0] : null },
      set(value) {
        if (!value) { this.queryObject.releaseTime = null; return }
        const end = this.mobileEndDate
        this.queryObject.releaseTime = [value, end && end >= value ? end : value]
      }
    },
    mobileEndDate: {
      get() { return this.queryObject.releaseTime ? this.queryObject.releaseTime[1] : null },
      set(value) {
        if (!value) { this.queryObject.releaseTime = null; return }
        const start = this.mobileStartDate
        this.queryObject.releaseTime = [start && start <= value ? start : value, value]
      }
    },
    activeFilterCount() {
      const q = this.queryObject
      return [q.gameId, q.name, q.author, q.releaseTime, q.type, q.language, q.tags.length ? q.tags : null].filter(Boolean).length
    }
  },
  mounted() {
    this.updateViewport()
    window.addEventListener('resize', this.updateViewport)
    if (window.visualViewport) window.visualViewport.addEventListener('resize', this.updateVisualViewport)
    if (typeof ResizeObserver !== 'undefined') {
      this.tableObserver = new ResizeObserver(() => this.updateTableHeight())
      this.tableObserver.observe(this.$refs.tableSpace)
    }
    this.$nextTick(this.updateTableHeight)
    this.loadOptions()
    this.fetchTable(1)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewport)
    if (window.visualViewport) window.visualViewport.removeEventListener('resize', this.updateVisualViewport)
    document.documentElement.style.removeProperty('--catalog-visible-height')
    if (this.tableObserver) this.tableObserver.disconnect()
  },
  methods: {
    applyDateShortcut(label) {
      this.queryObject.releaseTime = dateRangeFor(label).map(formatLocalDate)
    },
    loadOptions() {
      const endpoints = [
        ['makers', '/api/open/select/maker'],
        ['types', '/api/open/select/type'],
        ['languages', '/api/open/select/language'],
        ['orderBy', '/api/open/select/orderBy']
      ]
      endpoints.forEach(([key, url]) => axios.get(url).then(res => { if (res.data.code === 200) this[key] = res.data.data || [] }))
      axios.get('/api/open/select/genre').then(res => { if (res.data.code === 200) this.genres = res.data.data.genres || [] })
    },
    fetchTable(pageNo, refresh = false) {
      const requestQuery = refresh && this.lastRequestQuery
        ? { ...this.lastRequestQuery, pageNo: pageNo || 1 }
        : JSON.parse(JSON.stringify({ ...this.queryObject, pageNo: pageNo || 1 }))
      if (!refresh) {
        this.queryObject.pageNo = requestQuery.pageNo
        this.lastRequestQuery = requestQuery
      }
      const requestId = ++this.requestId
      this.loading = true
      axios.post('/api/open/table', requestQuery).then(res => {
        if (requestId !== this.requestId) return
        if (res.data.code !== 200 || !res.data.data) throw new Error('目录加载失败')
        this.tableData = res.data.data.rows || []
        this.total = res.data.data.total || 0
        this.totalPage = res.data.data.pageCount || 0
        this.current = res.data.data.current || requestQuery.pageNo
        this.$nextTick(() => {
          if (this.$refs.table) {
            this.$refs.table.doLayout()
            const tableBody = this.$refs.table.$el.querySelector('.el-table__body-wrapper')
            if (tableBody) tableBody.scrollTop = 0
          }
          if (this.$refs.mobileResults) this.$refs.mobileResults.$el.scrollTop = 0
        })
      }).catch(() => {
        if (requestId === this.requestId) this.$message.error('目录加载失败，请稍后重试')
      }).finally(() => { if (requestId === this.requestId) this.loading = false })
    },
    refreshTable() {
      this.fetchTable(this.current, true)
    },
    search() {
      this.mobileFiltersOpen = false
      this.fetchTable(1)
    },
    resetFilters() {
      Object.assign(this.queryObject, emptyFilters())
      this.search()
    },
    limitTags(value) {
      if (value.length <= 5) return
      this.queryObject.tags = value.slice(0, 5)
      this.$message.warning('最多选择 5 个分类')
    },
    updateViewport() {
      this.isMobile = window.innerWidth <= 767
      this.isCompactLandscape = window.innerWidth >= 768 && window.innerWidth <= 1200 && window.innerHeight <= 850
      this.updateVisualViewport()
      this.$nextTick(this.updateTableHeight)
    },
    updateVisualViewport() {
      const height = window.visualViewport ? window.visualViewport.height : window.innerHeight
      document.documentElement.style.setProperty('--catalog-visible-height', `${height}px`)
    },
    updateTableHeight() {
      if (this.$refs.tableSpace && this.$refs.tableSpace.clientHeight > 0) {
        this.tableHeight = this.$refs.tableSpace.clientHeight
        if (this.$refs.table) this.$refs.table.doLayout()
      }
    },
    workUrl(game) { return game.dlsiteUrl || 'https://www.dlsite.com/maniax/work/=/product_id/' + game.gameId + '.html' },
    typeParts(type) { return String(type || '').split('/').filter(Boolean) },
    formatNumber(value) { return Number(value || 0).toLocaleString() },
    formatScore(value) { return Number(value || 0).toFixed(2) },
    signedNumber(value) { const number = Number(value || 0); return number > 0 ? '+' + number : String(number) }
  }
}
</script>

<style scoped>
.catalog-page { height: 100%; min-height: 0; text-align: left; color: #303133; }
.catalog-layout { display: flex; flex-direction: column; gap: 10px; height: 100%; min-height: 0; }
.filter-card, .results-card { min-height: 0; border: 1px solid rgba(220,223,230,.8); border-radius: 17px; background: rgba(255,255,255,.58); box-shadow: 0 10px 30px rgba(0,0,0,.08); backdrop-filter: blur(8px); }
.filter-card { flex: none; }
.filter-card >>> .el-card__header { padding: 10px 18px 5px; border-bottom: 0; }
.filter-card >>> .el-card__body { padding: 0 12px; }
.filter-heading { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
.filter-heading strong { font-size: 16px; }
.filter-heading span { color: #909399; font-size: 11px; }
.filter-scroll { overflow: visible; }
.filter-form { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 4px 14px; padding: 0 6px 8px; }
.filter-form >>> .el-form-item { min-width: 0; margin: 0; }
.filter-form >>> .el-form-item__label { padding: 0 0 2px; line-height: 14px; color: #606266; font-size: 10px; font-weight: 600; }
.filter-form >>> .el-select, .filter-form >>> .el-date-editor, .filter-form >>> .el-cascader { width: 100%; }
.filter-form >>> .el-input__inner { background: rgba(255,255,255,.55); }
.filter-form >>> .el-date-editor.el-range-editor { background: rgba(255,255,255,.55); }
.filter-form >>> .el-date-editor .el-range-input,
.filter-form >>> .el-cascader__search-input { background: transparent; }
.filter-form >>> .el-cascader .el-input__inner::placeholder { color: transparent; }
.filter-actions { display: flex; align-self: end; gap: 8px; }
.filter-actions >>> .el-button { flex: 1; margin: 0; }
.results-card { flex: 1; min-height: 0; }
.results-card >>> .el-card__body { height: 100%; min-height: 0; box-sizing: border-box; padding: 0; display: flex; flex-direction: column; }
.results-heading { flex: none; padding: 23px 26px 19px; display: flex; align-items: end; justify-content: space-between; gap: 14px; border-bottom: 1px solid rgba(220,223,230,.7); background: linear-gradient(120deg, rgba(255,255,255,.35), rgba(236,245,255,.25)); border-radius: 17px 17px 0 0; }
.section-kicker { color: #409eff; font-size: 10px; font-weight: 800; letter-spacing: .18em; }
.results-title h1 { margin: 7px 0 0; font-size: 24px; line-height: 1.25; }
.results-footer strong { color: #409eff; }
.results-controls { display: flex; align-items: center; flex-wrap: wrap; justify-content: flex-end; gap: 10px; }
.results-controls label { display: inline-flex; align-items: center; gap: 6px; color: #909399; font-size: 11px; white-space: nowrap; }
.results-controls >>> .el-select { width: 122px; }
.results-controls label:nth-child(2) >>> .el-select { width: 68px; }
.results-controls >>> .el-input__inner { background: rgba(255,255,255,.55); }
.results-refresh { margin-left: 0; }
.table-space { flex: 1; min-height: 0; }
.table-space >>> .el-table { color: #606266; background: transparent; }
.table-space >>> .el-table th, .table-space >>> .el-table tr, .table-space >>> .el-table__expanded-cell { background: rgba(255,255,255,.24); }
.table-space >>> .el-table td, .table-space >>> .el-table th.is-leaf { border-bottom-color: rgba(220,223,230,.7); }
.table-space >>> .el-table--enable-row-hover .el-table__body tr:hover > td { background: rgba(64,158,255,.12); }
.table-space >>> .el-table__body-wrapper::-webkit-scrollbar { width: 7px; height: 7px; }
.table-space >>> .el-table__body-wrapper::-webkit-scrollbar-track { background: rgba(220,223,230,.5); }
.table-space >>> .el-table__body-wrapper::-webkit-scrollbar-thumb { background: #409eff; border-radius: 7px; }
.table-space >>> .el-table__body-wrapper { scrollbar-width: thin; scrollbar-color: #409eff rgba(220,223,230,.5); }
.table-work { display: flex; align-items: center; gap: 10px; min-width: 0; }
.table-cover { width: 80px; height: 60px; flex: none; background: #f5f7fa; border-radius: 7px; }
.cover-empty { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #909399; }
.table-work-text { min-width: 0; }
.work-ids { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; margin-bottom: 2px; }
.work-code { display: inline-block; max-width: 100%; padding: 1px 4px; border: 1px solid rgba(64,158,255,.22); border-radius: 4px; background: rgba(64,158,255,.08); color: #409eff; font-size: 10px; font-weight: 700; line-height: 14px; white-space: nowrap; }
.work-code.deleted { color: #f56c6c; }
.work-name { color: #303133; font-size: 12px; font-weight: 700; line-height: 1.4; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.work-translation { display: block; color: #909399; font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.table-tags { display: flex; flex-wrap: wrap; gap: 4px; }
.table-tags >>> .el-tag { border: 0; height: 18px; line-height: 18px; font-size: 10px; }
.score { color: #e6a23c; font-size: 11px; font-weight: 700; white-space: nowrap; }
.delta { display: block; color: #67c23a; font-size: 10px; }.delta.negative { color: #f56c6c; }
.price { color: #409eff; font: bold 16px Georgia, serif; white-space: nowrap; }.price small { font: 10px Arial, sans-serif; }
a { color: #409eff; text-decoration: none; } a:hover { color: #66b1ff; }
.results-footer { flex: none; min-height: 55px; padding: 10px 24px; box-sizing: border-box; border-top: 1px solid rgba(220,223,230,.7); display: flex; align-items: center; justify-content: space-between; gap: 8px; color: #909399; font-size: 11px; }
.mobile-filter-toggle, .mobile-results { display: none; }
@media (max-width: 1100px) { .filter-form { grid-template-columns: repeat(2, minmax(0, 1fr)); }.results-heading { align-items: start; flex-direction: column; }.results-controls { justify-content: flex-start; } }
@media (max-width: 900px) { .results-heading { padding: 18px; }.results-footer { padding: 10px; } }
@media (min-width: 768px) and (max-width: 1200px) and (max-height: 850px) {
  .catalog-page { position: relative; }
  .mobile-filter-toggle { display: flex; align-items: center; justify-content: space-between; width: 100%; height: 36px; margin-bottom: 8px; color: #409eff; border-color: #b3d8ff; background: rgba(255,255,255,.6); }
  .filter-count { display: inline-block; margin-left: 5px; padding: 0 5px; border-radius: 8px; color: #fff; background: #409eff; font-size: 10px; }
  .catalog-layout { height: calc(100% - 44px); }
  .filter-card { display: none; position: absolute; top: 44px; left: 0; right: 0; z-index: 10; max-height: min(390px, calc(100% - 52px)); flex-direction: column; }
  .filter-card.mobile-open { display: flex; }
  .filter-card >>> .el-card__body { display: flex; flex: 1; min-height: 0; flex-direction: column; }
  .filter-scroll { flex: 1; min-height: 0; overflow-y: auto; }
  .results-heading { align-items: center; flex-direction: row; padding: 11px 18px; }
  .results-title h1 { margin-top: 3px; font-size: 19px; }
  .results-controls { justify-content: flex-end; }
  .results-footer { min-height: 48px; padding: 7px 12px; }
}
@media (max-width: 767px) {
  .catalog-page { position: relative; }
  .mobile-filter-toggle { display: flex; align-items: center; justify-content: space-between; width: 100%; height: 36px; margin-bottom: 8px; color: #409eff; border-color: #b3d8ff; background: rgba(255,255,255,.6); }
  .filter-count { display: inline-block; margin-left: 5px; padding: 0 5px; border-radius: 8px; color: #fff; background: #409eff; font-size: 10px; }
  .catalog-layout { height: calc(100% - 44px); }
  .filter-card { display: none; flex-direction: column; height: min(400px, 55%); }
  .filter-card.mobile-open { display: flex; }
  .filter-card >>> .el-card__body { flex: 1; min-height: 0; display: flex; flex-direction: column; }
  .filter-scroll { flex: 1; min-height: 0; overflow-y: auto; }
  .filter-form { grid-template-columns: 1fr; gap: 10px; padding-bottom: 14px; }
  .mobile-date-range { display: flex; gap: 8px; min-width: 0; }
  .mobile-date-range >>> .el-date-editor { flex: 1; width: calc(50% - 4px) !important; min-width: 0; }
  .mobile-date-shortcuts { display: flex; gap: 5px; margin-top: 7px; padding-bottom: 2px; overflow-x: auto; scrollbar-width: thin; scrollbar-color: #409eff rgba(220,223,230,.5); }
  .mobile-date-shortcuts >>> .el-button { flex: none; margin: 0; padding: 5px 8px; }
  .results-heading { padding: 15px 12px; gap: 12px; }
  .results-title h1 { font-size: 20px; }
  .results-controls { gap: 6px 10px; }
  .results-refresh span { display: none; }
  .table-space { display: none; }
  .mobile-results { display: block; flex: 1; min-height: 0; padding: 12px; }
  .mobile-work-card { margin-bottom: 12px; border-color: rgba(220,223,230,.85); border-radius: 12px; background: rgba(255,255,255,.53); }
  .mobile-work-main { display: flex; gap: 11px; }
  .mobile-cover { width: 112px; min-width: 112px; height: 84px; align-self: center; border-radius: 7px; overflow: hidden; background: #f5f7fa; }
  .mobile-cover >>> .el-image { width: 100%; height: 100%; }
  .mobile-work-info { min-width: 0; flex: 1; display: flex; flex-direction: column; gap: 4px; }
  .mobile-work-name { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; line-height: 1.35; font-size: 12px; }
  .mobile-work-info .work-translation { white-space: normal; overflow: visible; text-overflow: clip; overflow-wrap: anywhere; line-height: 1.4; }
  .mobile-work-info .table-tags { margin-top: auto; }
  .mobile-work-meta, .mobile-work-bottom { display: flex; justify-content: space-between; align-items: end; gap: 8px; margin-top: 9px; font-size: 10px; color: #909399; }
  .mobile-work-bottom { padding-top: 8px; border-top: 1px solid rgba(220,223,230,.65); font-size: 11px; }
  .mobile-work-bottom > a, .mobile-work-bottom > span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .results-footer { min-height: 62px; padding: 7px 5px; flex-direction: column; justify-content: center; gap: 3px; }
  .results-footer >>> .el-pagination { width: 100%; text-align: center; white-space: nowrap; }
  .results-footer >>> .el-pagination .btn-prev, .results-footer >>> .el-pagination .btn-next, .results-footer >>> .el-pagination .el-pager li { min-width: 25px; margin: 0 2px; }
  .mobile-empty { padding: 50px 10px; text-align: center; color: #909399; font-size: 12px; }
}
</style>

<style>
@media (max-width: 767px) {
  .catalog-filter-popper.el-select-dropdown,
  .catalog-filter-popper.el-cascader__dropdown { left: 12px !important; width: calc(100vw - 24px) !important; min-width: 0 !important; max-width: calc(100vw - 24px); }
  .catalog-filter-popper .el-select-dropdown__wrap { max-height: clamp(96px, calc(var(--catalog-visible-height, 100dvh) - 270px), 180px) !important; }
  .catalog-filter-popper .el-cascader-panel { max-width: 100%; overflow-x: auto; }
  .catalog-mobile-date-popper.el-picker-panel { left: max(10px, calc(50vw - 162px)) !important; max-width: calc(100vw - 20px); }
}
</style>
